import { ChangeEvent, FC, useState, useEffect, useMemo } from 'react';
import {
    Flex,
    Box,
    Text,
    VStack,
    Button,
    Input,
    useToast,
    Grid,
    GridItem,
    Link,
    Select,
    Checkbox,
} from '@chakra-ui/react';
import { DEFAULT_USER, HEADER_COMPANY, USER_FORM_SKIPPED_FIELD } from 'pages/UserManagement/const';
import {
    IClientCategories,
    ICompanyGroups,
    ICompanyGroupV2,
    ITools,
    IToolsDetails,
    TTree,
} from 'pages/UserManagement/types';
import AMultiSelect, { MultiSelectOption } from 'components/atoms/AMultiSelect';
import {
    useAddCompanyGroupMutation,
    useUpdateCompanyGroupMutation,
    useDeleteCompanyGroupMutation,
} from 'core/API/usersApi';

import { intentionalyFloatingPromiseReturn } from 'utils/wrappers';
import { isFetchBaseQueryError } from 'utils/erros';
import { useTranslation } from 'helpers/languages/translations';
import { alphaNumericSort } from 'utils/strings';
import TreeView, { ITreeProps } from '../../atoms/TreeView';

const MCompanyGroupEdit: FC<{
    isAdd: boolean;
    values?: ICompanyGroups;
    toolsList: ITools[];
    toolsDetails?: IToolsDetails;
    defaultCompanyGroup?: number | null;
    onClose: () => void;
}> = ({ isAdd, values, toolsList, toolsDetails, onClose, defaultCompanyGroup }) => {
    const toast = useToast();
    const translate = useTranslation();
    const [deleteCompanyGroup, { isLoading: isDeleteCompanyGroupLoading }] = useDeleteCompanyGroupMutation();
    const [updateCompanyGroup, { isLoading: isUpdateCompanyGroupLoading }] = useUpdateCompanyGroupMutation();
    const [addCompanyGroupMutaion, { isLoading: isAddCompanyGroupLoading }] = useAddCompanyGroupMutation();

    const [toolsOptions, setToolsOptions] = useState<MultiSelectOption[]>([]);
    const [selectToolsOptions, setSelectedToolsOptions] = useState<MultiSelectOption[]>([]);
    const [checkToolItemsArray, setCheckToolItemsArray] = useState<boolean[][]>([]);
    const [checkRetailerItemsArray, setCheckRetailerItemsArray] = useState<boolean[]>([]);
    const [checkClientCategoriesItemsArray, setCheckClientCategoriesItemsArray] = useState<boolean[]>([]);
    const [fieldsValues, setFieldsValues] = useState<ICompanyGroups>({
        ...(values && !isAdd ? values : DEFAULT_USER),
    });

    const sortedRetailers = useMemo(() => {
        const sortedRetailersData = toolsDetails?.retailers.slice();
        if (sortedRetailersData) return sortedRetailersData.sort((a, b) => alphaNumericSort(a.name, b.name));
        return [];
    }, [toolsDetails]);

    const sortedTools = useMemo(() => {
        const sortedToolsData = toolsDetails?.tools.map((tool) => {
            return {
                ...tool,
                name: translate(`company.group.tool.${tool.name}`),
                tabs: tool.tabs.map((tab) => ({ ...tab, name: translate(`company.group.tab.${tab.name}`) })),
            };
        });

        if (sortedToolsData) {
            return sortedToolsData;
        }
        return [];
    }, [toolsDetails]);

    useEffect(() => {
        const toolsData = toolsList.map((tool) => ({ id: tool.id, name: tool.name }));

        if (isAdd) {
            const newFieldsValues = { ...fieldsValues };
            newFieldsValues.tools = toolsList.map((toolListdata) => toolListdata.id);
            newFieldsValues.tabs = toolsDetails
                ? toolsDetails.tools
                      .map((toolListdata) => {
                          return toolListdata.tabs.map((tab) => tab.id);
                      })
                      .reduce((a, b) => a.concat(b))
                : [];
            newFieldsValues.retailers = toolsDetails ? toolsDetails.retailers.map((ret) => ret.id) : [];
            newFieldsValues.categories_clients = toolsDetails
                ? toolsDetails.client_categories.map((cat) => cat.id)
                : [];
            setFieldsValues(newFieldsValues);
            setSelectedToolsOptions(toolsData);
        }

        setToolsOptions(toolsData);
        const toolsListMap = new Map<number, string>();
        toolsList.forEach((tool) => toolsListMap.set(tool.id, tool.name));

        if (values) {
            const selectedToolsData = values.tools.map((tool) => {
                const optionName = toolsListMap.get(tool);

                return {
                    id: tool,
                    name: optionName !== undefined ? optionName : '',
                };
            });

            setSelectedToolsOptions(selectedToolsData);
        }

        if (toolsDetails) {
            const checkedItems: boolean[][] = [];

            sortedTools.forEach((tool) => {
                const tabsChecks = tool.tabs.map((tab) => {
                    if (fieldsValues && fieldsValues.tabs.find((tabid) => tabid === tab.id)) return true;
                    return isAdd;
                });

                checkedItems.push(tabsChecks);
            });
            setCheckToolItemsArray(checkedItems);

            const retailersChecks = sortedRetailers.map((tool) => {
                if (fieldsValues && fieldsValues.retailers.find((tabid) => tabid === tool.id)) return true;
                return isAdd;
            });
            setCheckRetailerItemsArray(retailersChecks);

            const clientsCategoriessChecks = toolsDetails?.client_categories?.map((tool) => {
                if (fieldsValues && fieldsValues.categories_clients.find((tabid) => tabid === tool.id)) return true;
                return isAdd;
            });

            setCheckClientCategoriesItemsArray(clientsCategoriessChecks);
        }
    }, [toolsList, values, toolsDetails]);

    const handleChangeInput = (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>, field: string) => {
        if (event.target.value !== null) setFieldsValues({ ...fieldsValues, [field]: event.target.value });
    };

    const handleChangeCheckbox = (event: ChangeEvent<HTMLInputElement>, field: string) => {
        if (event.target.checked !== null) {
            setFieldsValues({ ...fieldsValues, [field]: event.target.checked });
        }
    };

    const handleDeleteCompanyGroup = async () => {
        try {
            const res = await deleteCompanyGroup({ id: [fieldsValues.id] });

            if ('error' in res) {
                const errorObj = res.error;
                if (isFetchBaseQueryError(errorObj)) {
                    const errorMsg: string =
                        'message' in (errorObj.data as any)
                            ? (errorObj.data as { message: string }).message
                            : JSON.stringify(errorObj.data);
                    throw new Error(errorMsg);
                }
                throw new Error(translate('cg.edit.delete.error') as string);
            }

            onClose();
            toast({
                title: translate('cg.edit.delete.success'),
                status: 'success',
                isClosable: true,
            });
        } catch {
            toast({
                title: translate('cg.edit.delete.error'),
                status: 'error',
                isClosable: true,
            });
        }
    };

    const handleUpdateUser = async () => {
        try {
            const formattedFieldsValues = {
                ...fieldsValues,
                categories_clients: undefined,
                client_categories: fieldsValues.categories_clients,
            } as ICompanyGroupV2;
            const res = isAdd
                ? await addCompanyGroupMutaion(formattedFieldsValues)
                : await updateCompanyGroup(formattedFieldsValues);

            if ('error' in res) {
                const errorObj = res.error;
                if (isFetchBaseQueryError(errorObj)) {
                    const errorMsg: string =
                        'message' in (errorObj.data as any)
                            ? (errorObj.data as { message: string }).message
                            : JSON.stringify(errorObj.data);
                    throw new Error(errorMsg);
                }
                throw new Error(translate(`cg.edit.${isAdd ? 'add' : 'update'}.success`) as string);
            }

            toast({
                title: translate(`cg.edit.${isAdd ? 'add' : 'update'}.success`),
                status: 'success',
                isClosable: true,
            });

            onClose();
        } catch {
            toast({
                title: translate(`cg.edit.${isAdd ? 'add' : 'update'}.error`),
                status: 'error',
                isClosable: true,
            });
        }
    };

    const formatDataTree = (
        parent: any | null,
        data: any[],
        accessors: string[],
        index: number,
        fieldsIndex: string,
    ) => {
        return data.map((row) => {
            const newRow = { ...row };

            newRow.isChecked =
                (fieldsValues[fieldsIndex as keyof typeof fieldsValues] as any[]).find(
                    (elem: any) => newRow.id == elem,
                ) !== undefined;
            newRow.id = `${row.id}${parent ? '_' + parent.id : ''}`;

            if (index + 1 < accessors.length && accessors[index + 1]) {
                newRow.children = formatDataTree(
                    newRow,
                    newRow[accessors[index + 1]],
                    accessors,
                    index + 1,
                    fieldsIndex,
                );
            }
            return newRow;
        });
    };

    const updateCheckStatus = (tree: TTree[], checkChildren = false) => {
        return tree.map((parent) => {
            if (parent.children && parent.children.length) {
                let isChecked = false;
                if (checkChildren) {
                    isChecked = parent.children.some((child) => child.isChecked);
                    parent.children = parent.children.map((child) => {
                        child.isChecked = isChecked;
                        return child;
                    });
                }

                const allChildrenChecked = isChecked || parent.children.every((child) => child.isChecked);
                parent.isChecked = allChildrenChecked;
            }
            return parent;
        });
    };

    const onChangeToolsTreeView = (data: any, treeView: any[]) => {
        const toolsRes = { ...fieldsValues };
        const newTabs = new Set<number>([...fieldsValues.tabs]);
        const newTools = new Set<number>([...fieldsValues.tools]);

        treeView.forEach((node) => {
            if (node.isChecked) {
                newTools.add(Number(node.id));
            } else {
                newTools.delete(Number(node.id));
            }

            if (node.children && node.children.length) {
                node.children.forEach((child: any) => {
                    if (child.isChecked) {
                        newTabs.add(Number(child.id.split('_')[0]));
                    } else {
                        newTabs.delete(Number(child.id.split('_')[0]));
                    }
                });
            }
        });

        toolsRes.tabs = Array.from(newTabs);
        toolsRes.tools = Array.from(newTools);
        setFieldsValues(toolsRes);
    };

    const onChangeRetailersTreeView = (data: any, treeView: any[]) => {
        const toolsRes = { ...fieldsValues };
        const newRetailers = new Set<number>([...fieldsValues.retailers]);

        treeView.forEach((node) => {
            if (node.isChecked) {
                newRetailers.add(Number(node.id));
            } else {
                newRetailers.delete(Number(node.id));
            }
        });

        toolsRes.retailers = Array.from(newRetailers);
        setFieldsValues(toolsRes);
    };

    const renderToolsDetail = () => {
        const treeData = updateCheckStatus(
            formatDataTree(null, sortedTools, ['tabs'], -1, 'tabs'),
            fieldsValues.is_custom_cg_sync_with_tools,
        );
        return (
            <TreeView
                treeData={treeData}
                childrenAccessor=""
                onChange={onChangeToolsTreeView}
                childrenDisabled={fieldsValues.is_custom_cg_sync_with_tools}
            />
        );
    };

    const renderRetailersDetail = () => {
        return (
            <TreeView
                treeData={formatDataTree(null, sortedRetailers, [], -1, 'retailers')}
                childrenAccessor=""
                disabled={fieldsValues.is_custom_cg_sync_with_tools}
                onChange={onChangeRetailersTreeView}
            />
        );
    };

    interface ICatTree {
        id: string;
        id_cat: string;
        name: string;
        isChecked: boolean;
        children: Record<string, ICatTree>;
    }

    const fillCat = (
        res: Record<string, ICatTree>,
        toInsert: IClientCategories,
        index: number,
    ): Record<string, ICatTree> => {
        if (index > 3) return res;
        const keyLevel = `level_${index}` as keyof IClientCategories;
        const keyLevelName = `level_name_${index}` as keyof IClientCategories;
        if (!res[toInsert[keyLevel] as string]) {
            res[toInsert[keyLevel] as string] = {
                id: toInsert[keyLevel] as string,
                id_cat: toInsert[keyLevel] as string,
                name: toInsert[keyLevelName] as string,
                isChecked: fieldsValues.categories_clients.find((elem: any) => toInsert.id == elem) !== undefined,
                children: {},
            };

            fillCat(res[toInsert[keyLevel] as string].children, toInsert, index + 1);
        } else {
            fillCat(res[toInsert[keyLevel] as string].children, toInsert, index + 1);
        }

        return res;
    };

    const getCategDataFromTree = (resData: Record<string, ICatTree>) => {
        return Object.keys(resData).map((categ) => {
            const elem: any = { ...resData[categ] };
            elem.children = getCategDataFromTree(elem.children);
            return elem;
        });
    };

    const formatCategData = (categs: IClientCategories[]) => {
        let res: Record<string, ICatTree> = {};

        categs?.forEach((categ) => {
            res = fillCat(res, categ, 1);
        });

        return getCategDataFromTree(res);
    };

    const searchElem = (tree: any[], check: Set<number>): any => {
        return tree.map((elem: any) => {
            if (elem.children && elem.children.length) {
                return searchElem(elem.children, check);
            } else if (elem.isChecked) {
                check.add(elem.id);
            }
        });
    };

    const onChangeCategsTreeView = (data: any, treeView: any[]) => {
        const toolsRes = { ...fieldsValues };
        const newCategories = new Set<number>();
        const check = new Set<number>();

        searchElem(treeView, check);

        check.forEach((categ) => {
            const clientCateg = toolsDetails?.client_categories.find((elem) => String(categ) === elem.level_3);
            if (clientCateg) {
                newCategories.add(clientCateg.id);
            }
        });

        toolsRes.categories_clients = Array.from(newCategories);
        setFieldsValues(toolsRes);
    };

    const renderClientsCategoriesDetail = () => {
        if (toolsDetails) {
            const catFormatCategTree = formatCategData(toolsDetails?.client_categories);
            return (
                <TreeView
                    treeData={catFormatCategTree}
                    disabled={fieldsValues.is_custom_cg_sync_with_tools}
                    childrenAccessor=""
                    onChange={onChangeCategsTreeView}
                />
            );
        }

        return [];
    };

    const onChangeSyncWithTool = (e: ChangeEvent<HTMLSelectElement>) => {
        const newValues = { ...fieldsValues };
        newValues.is_custom_cg_sync_with_tools = e.target.value === 'true';
        newValues.sync_with_tools = newValues.is_custom_cg_sync_with_tools;
        setFieldsValues(newValues);
    };

    const checkApplyForm = () => {
        const toolsItems = sortedTools
            .map((tool, toolsIndex) => checkToolItemsArray.length && checkToolItemsArray[toolsIndex].some(Boolean))
            .some(Boolean);

        const isValid = [fieldsValues.name.length === 0, fieldsValues.tools.length === 0].some(Boolean);

        return isValid;
    };

    const createFieldList = () => {
        return (
            <>
                <GridItem w="100%" colSpan={2}>
                    <VStack minH="40px" w="100%" alignItems="start" fontSize="sm">
                        <Text as={'b'}>{translate(HEADER_COMPANY.name)}</Text>
                        <Input
                            onChange={(e) => handleChangeInput(e, 'name')}
                            size="sm"
                            value={fieldsValues && fieldsValues.name}
                        />
                    </VStack>
                </GridItem>
                <GridItem w="100%" colSpan={1}>
                    <VStack minH="40px" w="100%" alignItems="start" fontSize="sm">
                        <Text as={'b'}>Type</Text>
                        <Select
                            value={fieldsValues.is_custom_cg_sync_with_tools ? 'true' : 'false'}
                            onChange={onChangeSyncWithTool}
                            size="sm"
                        >
                            <option value="false">{translate('cg.tools.detailed')}</option>
                            <option value="true">{translate('cg.tools.only')}</option>
                        </Select>
                    </VStack>
                </GridItem>
                <GridItem w="100%" colSpan={3} display="flex" alignItems="center">
                    <Checkbox
                        isChecked={fieldsValues.is_default}
                        onChange={(e) => handleChangeCheckbox(e, 'is_default')}
                        size="md"
                        isDisabled={fieldsValues.id === defaultCompanyGroup}
                    />
                    <Text as="span" ml="5px">
                        {translate('cg.modal.field.default')}
                    </Text>
                </GridItem>
                <GridItem w="100%" colSpan={1}>
                    <VStack minH="40px" w="100%" alignItems="start" fontSize="sm">
                        <Text as={'b'}>{translate(HEADER_COMPANY.tools)}</Text>
                        <Box width="100%" border="1px" borderRadius="md" borderColor="gray.300" paddingTop={4}>
                            {renderToolsDetail()}
                        </Box>
                    </VStack>
                </GridItem>
                <GridItem w="100%" colSpan={1}>
                    <VStack minH="40px" w="100%" alignItems="start" fontSize="sm">
                        <Text as={'b'}>{translate(HEADER_COMPANY.retailers)}</Text>
                        <Box width="100%" border="1px" borderRadius="md" borderColor="gray.300" paddingTop={4}>
                            {renderRetailersDetail()}
                        </Box>
                    </VStack>
                </GridItem>
                <GridItem w="100%" colSpan={1}>
                    <VStack minH="40px" w="100%" alignItems="start" fontSize="sm">
                        <Text as={'b'}>{translate(HEADER_COMPANY.categories_clients)}</Text>
                        <Box width="100%" border="1px" borderRadius="md" borderColor="gray.300" paddingTop={4}>
                            {renderClientsCategoriesDetail()}
                        </Box>
                    </VStack>
                </GridItem>
            </>
        );
    };

    return (
        <Box>
            <Grid templateColumns="repeat(3, 1fr)" gap={6}>
                {createFieldList()}
            </Grid>

            <Flex direction="row" marginTop={8} flex="1" justifyContent="flex-space-between">
                {!isAdd && (
                    <Button
                        isLoading={isDeleteCompanyGroupLoading}
                        onClick={intentionalyFloatingPromiseReturn(handleDeleteCompanyGroup)}
                        size="sm"
                        variant="outline"
                        colorScheme="red"
                        mr={3}
                    >
                        {translate('cg.edit.modal.delete.button')}
                    </Button>
                )}
                <Flex flex="1" justifyContent="flex-end">
                    {(fieldsValues || isAdd) && (
                        <>
                            <Button
                                isDisabled={checkApplyForm()}
                                onClick={intentionalyFloatingPromiseReturn(handleUpdateUser)}
                                size="sm"
                                colorScheme="blue"
                                bg="blueMain"
                                isLoading={
                                    isUpdateCompanyGroupLoading ||
                                    isAddCompanyGroupLoading ||
                                    isDeleteCompanyGroupLoading
                                }
                            >
                                {translate('cg.edit.modal.ok')}
                            </Button>
                        </>
                    )}
                    <Button onClick={onClose} size="sm" variant="outline" mr={3} marginLeft={3}>
                        {translate('cg.edit.modal.cancel')}
                    </Button>
                </Flex>
            </Flex>
        </Box>
    );
};

export default MCompanyGroupEdit;
